import Modal from 'react-modal';
import { MODAL_CONTENT_DEFAULTS, MODAL_OVERLAY_DEFAULTS, UserCancellationError } from './utils.ts';
import { CloseButton } from './CloseButton.tsx';
import { useLoginSession } from './SessionContext.tsx';
import { PaywallTrigger, useSubscriptionContext } from './SubscriptionContext.tsx';
import { useRcAdapter } from './GlobalState.tsx';
import { Browser } from '@capacitor/browser';
import './Account.css';
import { useInGameNotificationContext } from './InGameNotificationContext.tsx';
import { track } from './umami.ts';
import * as Sentry from '@sentry/capacitor';

export function Account({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    const loginSession = useLoginSession();
    const subscriptionContext = useSubscriptionContext();
    const rcAdapter = useRcAdapter();
    const inGameNotificationsContext = useInGameNotificationContext();

    function manageSubscription(): Promise<void> {
        return rcAdapter.getSubscriptionManagementUrl(loginSession.getUserId()!).then((url) => {
            return Browser.open({ url });
        });
    }

    function signOut() {
        onClose();
        loginSession.signOut();
        inGameNotificationsContext.displayNotification('Successfully signed out', 2000);
        console.info('User signed out');
        track('user_signed_out');
    }

    return (
        <div className='account-container'>
            <Modal
                isOpen={isOpen}
                onRequestClose={onClose}
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: '250px',
                        margin: 'auto auto',
                        height: 'fit-content',
                        maxHeight: '90%',
                        overflowY: 'auto',
                        padding: '30px',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}>
                <CloseButton onClose={onClose} />
                <div className='vertical-flex-container'>
                    <h2 style={{ textAlign: 'center' }}>Account Info</h2>
                    <div>{loginSession?.session?.user?.email}</div>
                    <div className='account-info-container'>
                        <div className='key-value-pair'>
                            <div>User since:</div>
                            <div>
                                {loginSession?.session?.user?.created_at
                                    ? new Date(loginSession.session.user.created_at).toLocaleDateString()
                                    : 'N/A'}
                            </div>
                        </div>
                        <div className='key-value-pair'>
                            <div>Tier:</div>
                            <div>{subscriptionContext.isPro ? 'Pro' : 'Free'}</div>
                        </div>
                    </div>
                    <div className='account-button-container'>
                        {subscriptionContext.isPro ? (
                            <button
                                className='game-button play-button fit-content-width-button button-primary cursor-pointer'
                                onClick={() => void manageSubscription()}>
                                Manage Subscription
                            </button>
                        ) : (
                            <button
                                className='game-button play-button fit-content-width-button button-primary cursor-pointer'
                                onClick={async () => {
                                    try {
                                        track('user_initiated_pro_upgrade', {});
                                        await subscriptionContext.ensureProLevel(PaywallTrigger.USER_INITIATED_UPGRADE);
                                    } catch (e) {
                                        if (!(e instanceof UserCancellationError)) {
                                            console.error('User-initiated Pro upgrade failed:', e);
                                            Sentry.captureException(e);
                                            inGameNotificationsContext.displayNotification(
                                                'An unexpected error occurred. Please try again later.',
                                                4000
                                            );
                                        } else {
                                            console.info('User cancelled Pro upgrade');
                                        }
                                    }
                                }}>
                                Upgrade to Pro
                            </button>
                        )}
                        <button
                            className='game-button play-button fit-content-width-button button-secondary cursor-pointer'
                            onClick={() =>
                                void subscriptionContext.refreshProStatus().then(() => {
                                    inGameNotificationsContext.displayNotification('Pro status refreshed', 2000);
                                })
                            }>
                            Refresh status
                        </button>
                        <button
                            className='game-button play-button fit-content-width-button button-secondary cursor-pointer'
                            onClick={() => signOut()}>
                            Sign out
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
